import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl} from "@angular/forms";
import {ApplicationInstance} from "../../../../../services/model/new-application.model";
import {distinctUntilChanged, Subscription, switchMap, tap} from "rxjs";
import {
	ApplicationDetailData,
	ApplicationDetailService
} from "../../../../../services/front/application-detail.service";
import {NewApplicationService} from "../../../../../services/back/new-application.service";
import {TranslateService} from "@ngx-translate/core";
import {SnackbarService} from "../../../../../services/front/snackbar.service";
import {EXTENSION_URL, SETTINGS_URL} from "../../../../../models/home/navigation.model";
import {Router} from "@angular/router";
import {RightSliderService} from "../../../../../services/front/right-slider.service";
import {TenantExtensionSettingsWithoutCredentials, TenantService} from "../../../../../services/tenant.service";

@Component({
  selector: 'app-application-settings',
  standalone: false,
  templateUrl: './application-settings.component.html',
  styleUrls: ['./application-settings.component.scss']
})
export class ApplicationSettingsComponent implements OnInit, OnDestroy {
	tenantId: string;
	isEditor: boolean;
	application: ApplicationInstance;
	extensionSettings: TenantExtensionSettingsWithoutCredentials;

	initialized: boolean = false;

	displayExtensionControl: FormControl<boolean> = new FormControl<boolean>(true, { nonNullable: true });

	subscription: Subscription = new Subscription();

	constructor(private applicationDetailService: ApplicationDetailService,
				private applicationService: NewApplicationService,
				private translate: TranslateService,
				private router: Router,
				private tenantService: TenantService,
				private rightSliderService: RightSliderService,
				private snackBar: SnackbarService) {
	}

	ngOnInit() {
		this.subscription.add(this.applicationDetailService.getApplicationDetailDataChanges()
			.pipe(tap(data => this.setApplicationDetailData(data)))
			.subscribe(() => this.initialize()));

		this.subscription.add(this.displayExtensionControl.valueChanges
			.pipe(
				distinctUntilChanged(),
				switchMap(displayedInExtension => this.applicationService.updateApplicationsDisplayed(this.tenantId,  [{ id: this.application.applicationId, displayed: displayedInExtension }])),
				tap(updateSucceed => this.displayUpdatedSucceed(updateSucceed)),
				tap(() => this.applicationDetailService.refreshApplicationInstance()),
			)
			.subscribe());
	}

	initialize() {
		this.subscription.add(this.tenantService.getTenantExtensionSettingsWithoutCredentials(this.tenantId)
			.pipe(tap(settings => {
				this.extensionSettings = settings
				if (!this.extensionSettings.applicationDisplayActivated)
					this.displayExtensionControl.disable({ emitEvent: false });
				else
					this.displayExtensionControl.enable({ emitEvent: false });
			}))
			.subscribe(() => this.initialized = true));
	}

	private setApplicationDetailData(data: ApplicationDetailData): void {
		this.tenantId = data.tenantId;
		this.isEditor = data.isEditor;
		this.application = data.instance;
		this.displayExtensionControl.setValue(this.application.displayedInExtension, { emitEvent: false });
	}

	displayUpdatedSucceed(updateSucceed: boolean): void {
		if (updateSucceed) {
			this.snackBar.show(this.translate.instant('page.application.detail.update.success'));
		} else {
			this.snackBar.show(this.translate.instant('page.application.detail.update.fail'), 'danger-snack');
		}
	}

	goToSettings() {
		this.router.navigate([EXTENSION_URL], { queryParams: { tenant: this.tenantId } }).then(() => this.rightSliderService.close());
	}

	goToTenantSettings() {
		if (!this.extensionSettings.applicationDisplayActivated)
			this.router.navigate([SETTINGS_URL], {queryParams: {tab: 'agent'}}).then(() => this.rightSliderService.close());
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
	}
}
